/**
 * Transforms technical errors into user-friendly messages.
 * Uses structured data (status codes, GraphQL codes) instead of fragile string matching.
 */

const HTTP_SERVER_ERROR_THRESHOLD = 500;

const STATUS_MESSAGES: Record<number, string> = {
  400: 'Please check all required fields are completed correctly.',
  401: 'Your session has expired. Please log in again.',
  403: "You don't have permission to perform this action.",
  404: 'The requested item was not found.',
  429: 'Too many requests. Please wait a moment and try again.',
};

const GRAPHQL_MESSAGES: Record<string, string> = {
  BAD_USER_INPUT: 'Please check all required fields are completed correctly.',
  FORBIDDEN: "You don't have permission to perform this action.",
  INTERNAL_SERVER_ERROR: 'The service is temporarily unavailable. Please try again in a few minutes.',
  UNAUTHENTICATED: 'Your session has expired. Please log in again.',
  VALIDATION_ERROR: 'Please check all required fields are completed correctly.',
};

const DEFAULT_MESSAGE = 'Something went wrong. Please try again or cut a ticket to the Journeys team if the problem continues.';
const SERVER_ERROR_MESSAGE = 'The service is temporarily unavailable. Please try again in a few minutes.';
const NETWORK_ERROR_MESSAGE = 'Unable to connect to the service. Please check your internet connection and try again.';

function logError(error: unknown, context?: string): void {
  const safeErrorData = {
    code: extractGraphQLCode(error),
    context,
    isNetworkError: isNetworkError(error),
    status: extractStatus(error),
    timestamp: new Date().toISOString(),
  };

  console.error('[Error]', safeErrorData);
}

/**
 * Generates a brief summary for HTTP status code errors.
 */
function getHttpErrorSummary(status: number): string {
  if (status === 400) return 'Bad Request';
  if (status === 401) return 'Unauthorized';
  if (status === 403) return 'Access Denied';
  if (status === 404) return 'Not Found';
  if (status === 429) return 'Rate Limited';
  if (status >= HTTP_SERVER_ERROR_THRESHOLD) return 'Server Error';
  return `HTTP ${status}`;
}

/**
 * Generates a brief summary for GraphQL error codes.
 */
function getGraphQLErrorSummary(code: string): string {
  if (code === 'FORBIDDEN') return 'Access Denied';
  if (code === 'BAD_USER_INPUT') return 'Validation Error';
  if (code === 'INTERNAL_SERVER_ERROR') return 'Server Error';
  if (code === 'UNAUTHENTICATED') return 'Authentication Error';
  if (code === 'VALIDATION_ERROR') return 'Validation Error';
  return `GraphQL ${code}`;
}

/**
 * Generates a brief summary of an error for display in user interfaces.
 * Returns concise, user-friendly error descriptions based on error type.
 */
export function getBriefErrorSummary(error: unknown): string {
  const status = extractStatus(error);
  if (typeof status === 'number') {
    return getHttpErrorSummary(status);
  }

  const code = extractGraphQLCode(error);
  if (code) {
    return getGraphQLErrorSummary(code);
  }

  if (isNetworkError(error)) {
    return 'Network Error';
  }

  const rawMessage = extractRawErrorMessage(error);
  const firstLine = rawMessage.split('\n')[0]?.trim();
  return firstLine || 'Unknown Error';
}

export function extractRawErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const errorObj = error as {
      message: unknown;
    };
    if (typeof errorObj.message === 'string' && errorObj.message.trim().length > 0) {
      return errorObj.message.trim();
    }
  }
  return 'An unknown error occurred';
}

export function transformError(error: unknown, context?: string): string {
  logError(error, context);

  const status = extractStatus(error);
  if (typeof status === 'number') {
    const message = STATUS_MESSAGES[status] ?? (status >= HTTP_SERVER_ERROR_THRESHOLD ? SERVER_ERROR_MESSAGE : null);
    if (message) return addContext(message, context);
  }

  const code = extractGraphQLCode(error);
  if (code && GRAPHQL_MESSAGES[code]) {
    return addContext(GRAPHQL_MESSAGES[code], context);
  }

  if (isNetworkError(error)) {
    return addContext(NETWORK_ERROR_MESSAGE, context);
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const errorObj = error as {
      message: unknown;
    };
    if (typeof errorObj.message === 'string' && errorObj.message.trim().length > 0) {
      return formatSimpleErrorMessage(errorObj.message, context);
    }
  }

  return addContext(DEFAULT_MESSAGE, context);
}

function extractStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) {
    return;
  }

  const errorObj = error as Record<string, unknown>;

  if (typeof errorObj.status === 'number') {
    return errorObj.status;
  }

  if (typeof errorObj.statusCode === 'number') {
    return errorObj.statusCode;
  }

  if (
    typeof errorObj.response === 'object' &&
    errorObj.response !== null &&
    typeof (errorObj.response as Record<string, unknown>).status === 'number'
  ) {
    return (errorObj.response as Record<string, unknown>).status as number;
  }

  return;
}

function extractGraphQLCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) {
    return;
  }

  const errorObj = error as Record<string, unknown>;

  if (typeof errorObj.extensions === 'object' && errorObj.extensions !== null) {
    const code = (errorObj.extensions as Record<string, unknown>).code;
    if (typeof code === 'string') {
      return code;
    }
  }

  if (typeof errorObj.errorCode === 'string') {
    return errorObj.errorCode;
  }

  if (typeof errorObj.code === 'string') {
    return errorObj.code;
  }

  return;
}

function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (typeof error === 'object' && error !== null) {
    return (error as Record<string, unknown>).name === 'NetworkError';
  }
  return false;
}

function addContext(message: string, context?: string): string {
  if (!context) return message;
  return `Error ${context.toLowerCase()}. ${message}`;
}

function formatSimpleErrorMessage(message: string, context?: string): string {
  const baseMessage = context ? `Something went wrong while ${context.toLowerCase()}: ${message}.` : `Something went wrong: ${message}.`;

  return `${baseMessage} Please try again or cut a ticket to the Journeys team if the problem continues.`;
}
